<template>
  <div class="viewContainer receiptMatcherView">
    <div class="receiptMatcherHeader">
      <div class="receiptMatcherTitle">
        <BackButton />
        <h1>{{ $t('receiptMatcher.title') }}</h1>
      </div>
      <div class="receiptMatcherControls">
        <b-button-group class="toggleButtons sheetTypeToggle">
          <b-button @click="selectedSheetType = 'outbound'" :class="selectedSheetType === 'outbound' ? 'active' : ''">
            {{ $t('receiptMatcher.outbound') }}
          </b-button>
          <b-button @click="selectedSheetType = 'inbound'" :class="selectedSheetType === 'inbound' ? 'active' : ''">
            {{ $t('receiptMatcher.inbound') }}
          </b-button>
        </b-button-group>

        <b-form-datepicker v-if="selectedSheetType === 'outbound'" v-model="sheetReceiptDate" :locale="$i18n.locale"
          :placeholder="$t('receiptMatcher.selectDatePlaceholder')" class="filterInput" />

        <b-form-input v-if="selectedSheetType === 'inbound'" v-model="sheetInvoiceNumber"
          :placeholder="$t('receiptMatcher.invoiceNumberPlaceholder')" class="filterInput" />

        <b-form-datepicker v-if="selectedSheetType === 'inbound'" v-model="sheetInvoiceDate" :locale="$i18n.locale"
          :placeholder="$t('receiptMatcher.selectDatePlaceholder')" class="filterInput" />
      </div>
    </div>

    <div class="receiptMatcherTables">
      <!-- LEFT TABLE: SheetRows -->
      <div class="receiptMatcherTableContainer">
        <div class="tableHeader">
          <div class="tableTitleGroup">
            <h2>{{ sheetRowsTitle }}</h2>
            <DocumentDownload :sourceFiles="sheetSourceFiles" :sheetType="selectedSheetType"
              :invoiceNumber="sheetInvoiceNumber" :receiptDate="sheetReceiptDate" />
          </div>
          <div class="tableSearchWrapper">
            <b-form-input v-model="sheetRowSearch" :placeholder="$t('receiptMatcher.searchSheetRows')"
              class="tableSearch wineSearch" />
            <span v-if="sheetRowSearch != null && sheetRowSearch.length > 0" class="tableSearchReset"
              @click="sheetRowSearch = ''">
              <svg width="13" height="13" viewBox="0 0 13 13" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M10.728 2.243a6.002 6.002 0 0 1 0 8.485 6.002 6.002 0 0 1-8.485 0 6.002 6.002 0 0 1 0-8.485 6.002 6.002 0 0 1 8.485 0zM9.116 4.788l-.848-.848L6.57 5.637 4.874 3.94l-.849.848 1.697 1.697-1.697 1.697.849.849L6.57 7.334 8.268 9.03l.848-.849L7.42 6.485l1.697-1.697z"
                  fill="#CED4DA" fill-rule="nonzero" />
              </svg>
            </span>
          </div>
        </div>
        <div class="tableScrollContainer">
          <b-table ref="sheetRowTable" :items="filteredSheetRows" :fields="sheetRowFields" small sticky-header
            selectable select-mode="single" :busy="saving" :tbody-tr-attr="sheetRowTrAttr"
            @row-clicked="toggleSheetRowDetails">
            <template #cell(expand)="data">
              <b-icon :icon="data.item._showDetails ? 'chevron-up' : 'chevron-down'"
                @click.stop="data.toggleDetails()" />
            </template>
            <template #cell(articleName)="data">
              <span>{{ data.value }}</span>
            </template>
            <template #cell(venueScoreMatch)="data">
              <VenueScoreStatusButton :score="data.item.pqVenueWineScore" :venueWineId="data.item.pqVenueWineId"
                :updaterStatus="data.item.updaterStatus"
                @clicked="(payload) => onVenueScoreStatusBtnClick(payload, data.item)" />
            </template>
            <template #row-details="data">
              <SheetRowDetails :sheetRow="data.item" :sheetType="selectedSheetType" @revert-match="onRevertMatch"
                @save="saveSingleSheetRow" @import="importSingleSheetRow" @create="onAddProduct"
                @ignore="ignoreSingleSheetRow" />
            </template>
          </b-table>
          <LoadingAnimation v-if="loadingSheetRows" />
          <div v-if="showSheetRowsHelp" class="sheetRowsHelp">
            {{ $t('receiptMatcher.sheetRowsHelp') }}
          </div>

          <ReceiptActionBar v-if="!showSheetRowsHelp" :disabled="loadingSheetRows || loadingVenueWines" :saving="saving"
            @save="saveAllSheetRows" :canSaveAll="canSaveAllPositions" :canImportAll="canImportAllPositions"
            @import="importAllSheetRows" />

        </div>
      </div>

      <!-- RIGHT TABLE: VenueWines -->
      <div class="receiptMatcherTableContainer">
        <div class="tableHeader">
          <h2>{{ $t('receiptMatcher.venueWines') }}</h2>
          <b-form-input v-model="venueWineSearch" :placeholder="$t('receiptMatcher.searchVenueWines')"
            class="tableSearch wineSearch" />
        </div>
        <div class="tableScrollContainer">
          <b-table ref="venueWineTable" :items="filteredVenueWines" :fields="venueWineFields"
            :per-page="venueWinesPerPage" :current-page="venueWinesCurrentPage" small sticky-header selectable
            select-mode="single" :busy="saving" :tbody-tr-attr="venueWineTrAttr" @row-clicked="toggleVenueWineDetails">
            <template #cell(expand)="data">
              <b-icon :icon="data.item._showDetails ? 'chevron-up' : 'chevron-down'"
                @click.stop="data.toggleDetails()" />
            </template>
            <template #cell(winery)="data">
              <span>{{ data.value }}</span>
            </template>
            <template #cell(region)="data">
              <span>{{ data.value }}</span>
            </template>
            <template #cell(attributes.amountFloat)="data">
              <span>{{ data.value }}</span>
            </template>
            <template #cell(attributes.title)="data">
              <span>
                <span :class="{ deletedVenueWine: isVenueWineDeleted(data.item) }">{{ data.value }}</span><template v-if="isVenueWineDeleted(data.item)"> ({{ $t('receiptMatcher.deletedLabel') }})</template>
              </span>
            </template>
            <template #row-details="data">
              <VenueWineDetails :venueWine="data.item" />
            </template>
          </b-table>
          <LoadingAnimation v-if="loadingVenueWines" />

          <div class="venueWineFooter">
            <button id="addNewWine" @click="onAddProduct()" class="iconButton add">{{ $t('winelist.addWine') }}</button>

            <b-pagination v-if="venueWinesTotalRows > venueWinesPerPage" v-model="venueWinesCurrentPage"
              :total-rows="venueWinesTotalRows" :per-page="venueWinesPerPage" aria-controls="venueWineTable" limit="4"
              align="right" prev-text="" next-text="" first-text="" last-text="" hide-ellipsis></b-pagination>
          </div>
        </div>
      </div>
    </div>

    <CreateWinesModal ref="createWinesModal" @addedNewVenueWines="handleAddedNewVenueWines" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DataService from '@/services/StrapiService'
import AiService from '@/services/PoolinqAiService'
import DataMappingService from '@/services/DataMappingService'
import DateTimeService from '@/services/DateTimeService'
import LoadingAnimation from '@/components/LoadingAnimation.vue'
import VenueScoreStatusButton from '@/components/receiptmatcher/VenueScoreStatusButton.vue'
import VenueWineDetails from '@/components/receiptmatcher/VenueWineDetails.vue'
import SheetRowDetails from '@/components/receiptmatcher/SheetRowDetails.vue'
import ReceiptActionBar from '@/components/receiptmatcher/ReceiptActionBar.vue'
import BackButton from '@/components/buttons/BackButton.vue'
import DocumentDownload from '@/components/receiptmatcher/DocumentDownload.vue'
import { filterVenueWineBySearchTerm } from '@/utils/venueWineFunctions'
import {
  filterSheetRowsForImport, filterSheetRowsForSave,
  UpdaterStatusSheetText,
} from '@/utils/sheetRowFunctions'
import * as Utils from '@/utils/functions'
import Tracking from '@/utils/tracking'
import CreateWinesModal from '@/components/modals/addWine/CreateWinesModal.vue'

export default {
  name: 'ReceiptMatcher',
  components: {
    LoadingAnimation,
    VenueScoreStatusButton,
    VenueWineDetails,
    SheetRowDetails,
    ReceiptActionBar,
    CreateWinesModal,
    BackButton,
    DocumentDownload,
  },
  props: {
    sheetType: String, // inbound or outbound
    receiptDate: String, // for outbound sheets
    invoiceNumber: String, // for inbound sheets
    invoiceDate: String, // for inbound sheets
  },
  data() {
    return {
      selectedSheetType: this.sheetType || 'outbound',
      sheetReceiptDate: this.toDatepickerFormat(this.receiptDate),
      sheetInvoiceNumber: this.invoiceNumber,
      sheetInvoiceDate: this.toDatepickerFormat(this.invoiceDate),
      sheetRows: [],
      venueWines: [],
      sheetRowSearch: '',
      venueWineSearch: '',
      loadingSheetRows: false,
      loadingVenueWines: false,
      saving: false,
      sheetId: null,
      sheetSourceFiles: [],
      draggedVenueWineId: null,
      venueWinesPerPage: 200,
      venueWinesCurrentPage: 1,
    }
  },
  computed: {
    ...mapGetters(['getVenue']),
    sheetRowsTitle() {
      const docType = this.selectedSheetType === 'outbound'
        ? this.$t('receiptMatcher.outbound_short')
        : this.$t('receiptMatcher.inbound_short')
      const receiptDateOrInvoiceNumber = this.selectedSheetType === 'outbound'
        ? DateTimeService.formatDateWithDots(this.formatInvoiceDate(this.sheetReceiptDate))
        : this.sheetInvoiceNumber
      return `${docType}: ${receiptDateOrInvoiceNumber || ''}`
    },
    showSheetRowsHelp() {
      return (
        !this.loadingSheetRows &&
        this.sheetRows.length === 0 &&
        (this.sheetInvoiceNumber == null || this.sheetInvoiceNumber === '') &&
        (this.sheetReceiptDate == null || this.sheetReceiptDate === '')
      )
    },
    sheetRowFields() {
      return [
        { key: 'expand', label: '' },
        { key: 'producer', label: this.$t('general.winery'), sortable: true },
        { key: 'articleName', label: this.$t('receiptMatcher.articleName'), sortable: true },
        { key: 'year', label: this.$t('general.vintage'), sortable: true },
        { key: 'venueScoreMatch', label: this.$t('receiptMatcher.pqVenueWineScore'), sortable: false },
      ]
    },
    venueWineFields() {
      return [
        { key: 'expand', label: '' },
        { key: 'attributes.wine.data.attributes.winery.data.attributes.title', label: this.$t('general.winery'), sortable: true },
        { key: 'attributes.title', label: this.$t('general.winetitle'), sortable: true },
        { key: 'attributes.year', label: this.$t('general.vintage'), sortable: true },
        { key: 'attributes.amountFloat', label: this.$t('general.amount'), sortable: true },
      ]
    },
    filteredSheetRows() {
      if (!this.sheetRowSearch || this.sheetRowSearch.trim() === '') {
        return this.sheetRows
      }
      const term = this.sheetRowSearch.trim().toLowerCase()
      return this.sheetRows.filter((row) => {
        return Object.values(row).some((value) => {
          if (value == null) return false
          return String(value).toLowerCase().includes(term)
        })
      })
    },
    filteredVenueWines() {
      const sheetRowVenueWineIds = this.sheetRowMatchedVenueWineIds
      const visibleVenueWines = this.venueWines.filter((vw) => {
        // Deleted venue wines are only shown when they are matched by a sheet row
        if (this.isVenueWineDeleted(vw) && !sheetRowVenueWineIds.has(Number(vw.id))) {
          return false
        }
        return true
      })
      if (!this.venueWineSearch || this.venueWineSearch.trim() === '') {
        return visibleVenueWines
      }
      return visibleVenueWines.filter((vw) => filterVenueWineBySearchTerm(vw, this.venueWineSearch))
    },
    /**
     * Collects the IDs of venue wines that are matched by sheet rows.
     */
    sheetRowMatchedVenueWineIds() {
      const ids = new Set()
      this.sheetRows.forEach((row) => {
        const id = row.pqVenueWineId
        if (id != null && id !== '' && id !== 'n/a') {
          ids.add(Number(id))
        }
      })
      return ids
    },
    venueWinesTotalRows() {
      return this.filteredVenueWines.length
    },
    sheetRowTrAttr() {
      return (item, type) => {
        if (type !== 'row') return {}
        const index = this.filteredSheetRows.indexOf(item)
        return { 'data-sheet-row-index': index }
      }
    },
    venueWineTrAttr() {
      return (item, type) => {
        if (type !== 'row') return {}
        return { draggable: 'true', 'data-venue-wine-id': item.id }
      }
    },
    canSaveAllPositions() {
      return filterSheetRowsForSave(this.sheetRows).length > 0
    },
    canImportAllPositions() {
      return filterSheetRowsForImport(this.sheetRows).length > 0
    },
  },
  methods: {
    /**
     * Synchronizes the browser URL query params with the current values in the
     * receipt matcher controls, so the URL always reflects what the user has
     * set (useful for sharing/bookmarking the current view).
     *
     * Only the params relevant to the active sheet type are kept; params of the
     * other sheet type are removed. The `pvid` and all other existing params
     * are preserved.
     */
    updateUrlParams() {
      const query = { ...this.$route.query }
      query.sheet_type = this.selectedSheetType
      if (this.selectedSheetType === 'outbound') {
        delete query.invoice_number
        delete query.invoice_date
        if (this.sheetReceiptDate) {
          query.receipt_date = this.sheetReceiptDate
        } else {
          delete query.receipt_date
        }
      } else {
        delete query.receipt_date
        if (this.sheetInvoiceNumber) {
          query.invoice_number = this.sheetInvoiceNumber
        } else {
          delete query.invoice_number
        }
        if (this.sheetInvoiceDate) {
          query.invoice_date = this.sheetInvoiceDate
        } else {
          delete query.invoice_date
        }
      }
      // Avoid redundant navigation to the same URL, which Vue Router rejects
      // with a NavigationDuplicated error.
      this.$router.replace({ query }).catch(() => { })
    },
    async loadSheetRows() {
      this.loadingSheetRows = true
      this.sheetRows = []
      this.sheetId = null
      this.sheetSourceFiles = []
      this.updateUrlParams()
      const venueId = this.getVenue.id
      try {
        let response
        if (this.selectedSheetType === 'outbound') {
          const receiptDate = this.formatInvoiceDate(this.sheetReceiptDate)
          response = await AiService.getOutboundSheetRows(venueId, receiptDate)
        } else {
          // The date input uses 'YYYY-MM-DD', but the service expects 'DD-MM-YYYY'
          const invoiceDate = this.formatInvoiceDate(this.sheetInvoiceDate)
          response = await AiService.getInboundSheetRows(venueId, this.sheetInvoiceNumber, invoiceDate)
        }
        const mapped = DataMappingService.mapSheetRows(response)
        this.sheetRows = mapped.rows
        this.sheetId = mapped.sheetId
        this.sheetSourceFiles = mapped.sourceFiles || []

        // Remember the original pqVenueWineId of every row, so a wrong match
        // can be reverted back to the value that was loaded from the sheet.
        // Also remember the original updater status, so it can be restored
        // when the user reverts a match.
        this.sheetRows.forEach((row) => {
          row.originalPqVenueWineId = row.pqVenueWineId
          row.originalUpdaterStatus = row.updaterStatus
        })
      } catch (err) {
        console.log(err)
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.loadError'),
          variant: 'danger',
        })
      } finally {
        this.loadingSheetRows = false
      }
    },
    async saveSingleSheetRow(sheetRow) {
      await this.saveAllSheetRows(sheetRow)
    },
    /**
     * Marks a single sheet row as ignored by setting its updater status to
     * the ignored text and saving it, so the updater will skip it.
     *
     * When `addToBlacklist` is true, the article name is also added to the
     * venue's outbound blacklist (the "Ignorier-Liste"), so the AI will
     * permanently skip this product on future receipts.
     */
    async ignoreSingleSheetRow(sheetRow, addToBlacklist = false) {
      if (this.saving) return
      if (this.sheetId == null) {
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.saveError'),
          variant: 'danger',
        })
        return
      }
      this.saving = true
      const venueId = this.getVenue.id
      // Remember the row index of the sheet row, so it can be found again
      // after the reload (loadSheetRows creates new SheetRow objects).
      const rowIndex = sheetRow != null ? sheetRow.rowIndex : null
      try {
        // Optionally add the article to the venue's outbound blacklist, so
        // the AI permanently skips it on future receipts.
        if (addToBlacklist && sheetRow.articleName) {
          const venue = Utils.deepCopyObject(this.getVenue)
          const blacklist = Array.isArray(venue.attributes.outboundBlacklist)
            ? venue.attributes.outboundBlacklist
            : []
          if (!blacklist.includes(sheetRow.articleName)) {
            blacklist.push(sheetRow.articleName)
            venue.attributes.outboundBlacklist = blacklist

            // Delete unnecessary relation attributes (same as SettingsModal)
            delete venue.attributes.playlists
            delete venue.attributes.smartLists
            delete venue.attributes.reports
            delete venue.attributes.wineries
            delete venue.attributes.wineSuppliers

            const response = await DataService.updateVenue(venue.id, venue)
            // Refresh the venue in the store, otherwise the next ignore would
            // start from a stale blacklist and overwrite the previous entry.
            if (response != null && response.data != null && response.data.data != null) {
              this.$store.commit('updateVenue', response.data.data)
            }
          }
        }

        sheetRow.updaterStatus = UpdaterStatusSheetText.Ignored
        await AiService.updateSheetRows(this.sheetId, venueId, [sheetRow])
        await this.loadSheetRows()
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.ignorePositionInfo'),
          variant: 'info',
        })
      } catch (err) {
        console.log(err)
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.saveError'),
          variant: 'danger',
        })
      } finally {
        this.saving = false
        if (rowIndex != null) {
          // loadSheetRows created new SheetRow objects, so find the updated
          // row by its rowIndex instead of toggling the stale reference.
          const updatedRow = this.sheetRows.find((r) => r.rowIndex === rowIndex)
          if (updatedRow != null) {
            this.toggleSheetRowDetails(updatedRow)
          }
        }
      }
    },
    /**
     * Save the current sheet rows to the AI backend, then reload the sheet
     * rows so the UI reflects the updated state.
     *
     * While the update and reload are running, an activity indicator is shown
     * and all interactions with both tables are disabled (via :busy on the
     * b-tables).
     */
    async saveAllSheetRows(singleSheetRow = null) {
      if (this.saving) return
      if (this.sheetId == null) {
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.saveError'),
          variant: 'danger',
        })
        return
      }

      const rowsToSave = singleSheetRow ? [singleSheetRow] : filterSheetRowsForSave(this.sheetRows)

      if (rowsToSave.length === 0) {
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.saveErrorNoChanges'),
          variant: 'warning',
        })
        return
      }

      this.saving = true
      const venueId = this.getVenue.id
      // Remember the row index of the sheet row, so it can be found again
      // after the reload (loadSheetRows creates new SheetRow objects).
      const rowIndex = singleSheetRow != null ? singleSheetRow.rowIndex : null
      try {
        await AiService.updateSheetRows(this.sheetId, venueId, rowsToSave)
        await this.loadSheetRows()
      } catch (err) {
        console.log(err)
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.saveError'),
          variant: 'danger',
        })
      } finally {
        this.saving = false
        if (rowIndex != null) {
          // loadSheetRows created new SheetRow objects, so find the updated
          // row by its rowIndex instead of toggling the stale reference.
          const updatedRow = this.sheetRows.find((r) => r.rowIndex === rowIndex)
          if (updatedRow != null) {
            this.toggleSheetRowDetails(updatedRow)
          }
        }
      }
    },
    /**
     * Runs the updater on a single sheet row
     * @param sheetRow 
     */
    async importSingleSheetRow(sheetRow) {
      await this.importAllSheetRows(sheetRow)
    },
    /**
     * Runs the updater on all sheet rows that 
     * can be imported (have a venue wine id and no updater status yet)
     * @param singleSheetRow 
     */
    async importAllSheetRows(singleSheetRow = null) {
      if (this.saving) return
      if (this.sheetId == null) {
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.importError'),
          variant: 'danger',
        })
        return
      }

      const importableRows = singleSheetRow ? [singleSheetRow] : filterSheetRowsForImport(this.sheetRows)

      if (importableRows.length === 0) {
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.importErrorNoRows'),
          variant: 'warning',
        })
        return
      }

      // Remember the row index of the sheet row, so it can be found again
      // after the reload (loadSheetRows creates new SheetRow objects).
      const rowIndex = singleSheetRow != null ? singleSheetRow.rowIndex : null

      this.saving = true

      try {
        const venueId = this.getVenue.id
        const sheetType = this.selectedSheetType
        // Run the updater for real. It runs asynchronously in a job queue on
        // the AI backend, so wait for the job to finish before reloading the
        // sheet rows (otherwise the updaterStatus column is not written yet).
        const importResponse = await AiService.startUpdaterWithSheet(venueId, this.sheetId, sheetType, importableRows, false)
        const importJobId = importResponse && importResponse.data && importResponse.data.job_id
        if (importJobId) {
          await AiService.waitForJobToFinish(importJobId)
        } else {
          // Fallback for backends that do not return a job id
          await new Promise((resolve) => setTimeout(resolve, 5000))
        }

        // The updater has finished, so the sheet now contains the updated
        // updaterStatus column. Reload the sheet rows and venue wines.
        await this.loadSheetRows()
        await new Promise((resolve) => setTimeout(resolve, 1000))
        await this.loadVenueWines()
      } catch (err) {
        console.log(err)
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.importError'),
          variant: 'danger',
        })
      } finally {
        this.saving = false
        if (rowIndex != null) {
          // loadSheetRows created new SheetRow objects, so find the updated
          // row by its rowIndex instead of toggling the stale reference.
          const updatedRow = this.sheetRows.find((r) => r.rowIndex === rowIndex)
          if (updatedRow != null) {
            this.toggleSheetRowDetails(updatedRow)
          }
        }
      }
    },
    async loadVenueWines() {
      this.loadingVenueWines = true
      try {
        const response = await DataService.getVenueWinesIncludingDeleted()
        this.venueWines = response['data'].data
      } catch (err) {
        console.log(err)
      } finally {
        this.loadingVenueWines = false
      }
    },
    /**
     * Converts a date to 'DD-MM-YYYY' (the format expected by the AI service).
     *
     * The date can arrive in two formats:
     *   - 'YYYY-MM-DD' from the <b-form-datepicker> input
     *   - 'DD-MM-YYYY' from a URL query param (e.g. invoice_date=06-07-2026)
     *
     * Only the 'YYYY-MM-DD' form is converted; an already-correct 'DD-MM-YYYY'
     * value is returned unchanged. Returns an empty string if the input is
     * empty or invalid.
     */
    formatInvoiceDate(date) {
      if (!date) return ''
      // Normalize dot separators (e.g. '19.08.2026') to dashes
      const normalized = String(date).replace(/\./g, '-')
      const parts = normalized.split('-')
      if (parts.length !== 3) return date
      // 'YYYY-MM-DD' → 'DD-MM-YYYY' (year first, 4 digits)
      if (parts[0].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`
      }
      // Already 'DD-MM-YYYY' (or 'D-M-YYYY') - leave as-is
      return normalized
    },
    /**
     * Converts a date to 'YYYY-MM-DD' (the format expected by the
     * <b-form-datepicker> input).
     *
     * The date can arrive in two formats:
     *   - 'DD-MM-YYYY' from a URL query param (e.g. invoice_date=06-07-2026)
     *   - 'YYYY-MM-DD' already in the datepicker format
     *
     * Only the 'DD-MM-YYYY' form is converted; an already-correct 'YYYY-MM-DD'
     * value is returned unchanged. Returns an empty string if the input is
     * empty or invalid.
     */
    toDatepickerFormat(date) {
      if (!date) return ''
      // Normalize dot separators (e.g. '19.08.2026') to dashes
      const normalized = String(date).replace(/\./g, '-')
      const parts = normalized.split('-')
      if (parts.length !== 3) return date
      // 'DD-MM-YYYY' → 'YYYY-MM-DD' (day first, 2 digits)
      if (parts[0].length === 2 && parts[2].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`
      }
      // Already 'YYYY-MM-DD' - leave as-is
      return normalized
    },
    /**
     * Waits for the DOM to settle after a re-render, so that any
     * collapse/expand transitions of detail rows have completed before
     * measuring positions for scrolling.
     */
    waitForSettle() {
      return new Promise((resolve) => {
        // Wait two animation frames so the browser has laid out the new DOM
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            resolve()
          })
        })
      })
    },
    toggleSheetRowDetails(row) {
      // Close all other sheet rows, then toggle the clicked one
      this.filteredSheetRows.forEach((r) => {
        if (r !== row) r._showDetails = false
      })
      row._showDetails = !row._showDetails

      // When opening, scroll the row to the top of the table
      if (row._showDetails) {
        this.waitForSettle().then(() => {
          this.scrollRowToTop(this.$refs.sheetRowTable, row, this.filteredSheetRows)
        })
      }
    },
    isVenueWineDeleted(vw) {
      return vw != null && vw.attributes != null && vw.attributes.deleted === true
    },
    toggleVenueWineDetails(vw) {
      // Close all other venue wines, then toggle the clicked one
      this.filteredVenueWines.forEach((v) => {
        if (v !== vw) v._showDetails = false
      })
      vw._showDetails = !vw._showDetails

      // When opening, scroll the row to the top of the table
      if (vw._showDetails) {
        // Switch to the page containing this venue wine
        const index = this.filteredVenueWines.indexOf(vw)
        if (index >= 0) {
          const page = Math.floor(index / this.venueWinesPerPage) + 1
          if (page !== this.venueWinesCurrentPage) {
            this.venueWinesCurrentPage = page
          }
        }
        this.waitForSettle().then(() => {
          this.scrollRowToTop(this.$refs.venueWineTable, vw, this.filteredVenueWines)
        })
      }
    },
    /**
     * Called when a drag starts on a venue wine row. Stores the venue wine id
     * in the dataTransfer (and on the component as a fallback) so it can be
     * read on drop.
     */
    onVenueWineDragStart(event) {
      const row = event.target.closest('tr')
      if (row == null) return
      const venueWineId = row.getAttribute('data-venue-wine-id')
      if (venueWineId == null) return
      this.draggedVenueWineId = Number(venueWineId)
      if (event.dataTransfer) {
        event.dataTransfer.setData('text/plain', venueWineId)
        event.dataTransfer.effectAllowed = 'copy'
      }
    },
    /**
     * Called when a venue wine is dragged over a sheet row. Prevents the
     * default so the drop is allowed, and highlights the row under the cursor
     * so the user can see the drop target.
     */
    onSheetRowDragOver(event) {
      event.preventDefault()
      const row = event.target.closest('tr')
      if (row == null) return
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy'
      }
    },
    /**
     * Called when a venue wine is dropped on a sheet row. Sets the sheet
     * row's pqVenueWineId to the dropped venue wine's id and shows an info
     * alert with the match.
     */
    onSheetRowDrop(event) {
      event.preventDefault()
      const row = event.target.closest('tr')
      if (row == null) return
      const index = row.getAttribute('data-sheet-row-index')
      if (index == null) return
      const sheetRow = this.filteredSheetRows[Number(index)]
      if (sheetRow == null) return

      // Read the venue wine id, falling back to the value captured on dragstart
      const venueWineId = event.dataTransfer
        ? event.dataTransfer.getData('text/plain')
        : null
      const resolvedId = venueWineId != null && venueWineId !== ''
        ? Number(venueWineId)
        : this.draggedVenueWineId
      if (resolvedId == null) return

      const venueWine = this.venueWines.find((vw) => Number(vw.id) === Number(resolvedId))
      if (venueWine == null) return

      const newVenueWineId = Number(resolvedId)
      // Nothing to do if the dropped venue wine is already the current match.
      if (Number(sheetRow.pqVenueWineId) === newVenueWineId) {
        this.draggedVenueWineId = null
        return
      }

      // Remember the original match on the first user change, so reverting
      // later can restore it instead of clearing the id.
      if (sheetRow.originalPqVenueWineId == null) {
        sheetRow.originalPqVenueWineId = sheetRow.pqVenueWineId
      }

      this.$set(sheetRow, 'pqVenueWineId', newVenueWineId)
      // Changing the match invalidates any previous updater status, so clear
      // the status column. The original status is restored when the user
      // reverts the match (see onRevertMatch).
      this.$set(sheetRow, 'updaterStatus', null)
      this.draggedVenueWineId = null

      this.$store.commit('setAlertText', {
        text: this.$t('receiptMatcher.matchInfo', {
          articleName: sheetRow.articleName,
          venueWineTitle: venueWine.attributes.title,
        }),
        variant: 'info',
      })
    },
    /**
     * Called when the user reverts a (wrong) match from a sheet row detail
     * view. Restores the sheet row's original pqVenueWineId.
     */
    onRevertMatch(sheetRow) {
      if (sheetRow == null) return
      this.$set(sheetRow, 'pqVenueWineId', sheetRow.originalPqVenueWineId)
      // Restore the updater status that was cleared when the match was
      // changed, so the row is back to its original state.
      this.$set(sheetRow, 'updaterStatus', sheetRow.originalUpdaterStatus)
      this.$store.commit('setAlertText', {
        text: this.$t('receiptMatcher.revertMatchInfo'),
        variant: 'info',
      })
    },
    /**
     * Returns the height of the sticky header (column titles) of a table's
     * scroll container, so rows can be scrolled below it.
     */
    getStickyHeaderHeight(tableRef) {
      if (tableRef == null) return 0
      const scroll = tableRef.$el.closest('.b-table-sticky-header')
      if (scroll == null) return 0
      const thead = scroll.querySelector('thead')
      return thead ? thead.getBoundingClientRect().height : 0
    },
    /**
     * Scrolls a single row to the top of its table's scroll container,
     * keeping it visible below the sticky column titles.
     */
    scrollRowToTop(tableRef, row, filteredList) {
      if (tableRef == null) return
      const scroll = tableRef.$el.closest('.b-table-sticky-header')
      if (scroll == null) return

      const index = filteredList.indexOf(row)
      if (index < 0) return

      const rows = Array.from(tableRef.$el.querySelectorAll('tbody tr'))
      // If the table is paginated, the row is at (index % perPage) on its page
      const rowIndex = tableRef === this.$refs.venueWineTable
        ? index % this.venueWinesPerPage
        : index
      const rowEl = rows[rowIndex]
      if (rowEl == null) return

      const headerHeight = this.getStickyHeaderHeight(tableRef)
      const rowOffset = rowEl.getBoundingClientRect().top - scroll.getBoundingClientRect().top - headerHeight
      scroll.scrollTo({
        top: scroll.scrollTop + rowOffset,
        behavior: 'smooth',
      })
    },
    /**
     * Callback when a VenueScoreStatusButton is clicked.
     * Finds the VenueWine with the same id in the right table, opens both
     * detail views, and scrolls both items to the top of their tables so the
     * user can see all the opened details.
     */
    onVenueScoreStatusBtnClick({ venueWineId }, sheetRow) {
      const targetId = Number(venueWineId)
      const targetVenueWine = this.venueWines.find((vw) => Number(vw.id) === targetId)
      if (targetVenueWine == null) {
        this.$store.commit('setAlertText', {
          text: this.$t('receiptMatcher.venueWineNotFound'),
          variant: 'danger',
        })
        return
      }

      // Close all other sheet rows, then open the clicked one
      this.filteredSheetRows.forEach((r) => {
        if (r !== sheetRow) r._showDetails = false
      })
      if (sheetRow != null) {
        this.$set(sheetRow, '_showDetails', true)
      }

      // Reset the venue wine search filter so the matching venue wine is
      // guaranteed to be part of the filtered list and its details can open.
      this.venueWineSearch = ''

      // Close all other venue wines, then open the matching one
      this.filteredVenueWines.forEach((v) => {
        if (v !== targetVenueWine) v._showDetails = false
      })
      this.$set(targetVenueWine, '_showDetails', true)

      // Scroll both items to the top of their tables so all details are visible
      this.waitForSettle().then(() => {
        this.scrollItemsToTop(sheetRow, targetVenueWine)
      })
    },
    /**
     * Scrolls both the sheet row and the venue wine row to the top of their
     * respective tables so the user can see all the opened details.
     */
    scrollItemsToTop(sheetRow, targetVenueWine) {
      const sheetTable = this.$refs.sheetRowTable
      const venueTable = this.$refs.venueWineTable
      if (sheetTable == null || venueTable == null) return

      // The .b-table-sticky-header is the PARENT wrapper of the table element
      const sheetScroll = sheetTable.$el.closest('.b-table-sticky-header')
      const venueScroll = venueTable.$el.closest('.b-table-sticky-header')
      if (sheetScroll == null || venueScroll == null) return

      // Find the index of the sheet row in its filtered list
      const sheetIndex = this.filteredSheetRows.indexOf(sheetRow)
      if (sheetIndex < 0) return

      // Find the index of the target venue wine in its filtered list
      const venueIndex = this.filteredVenueWines.indexOf(targetVenueWine)
      if (venueIndex < 0) return

      // If the venue wine is not on the current page, switch to its page
      const venuePage = Math.floor(venueIndex / this.venueWinesPerPage) + 1
      if (venuePage !== this.venueWinesCurrentPage) {
        this.venueWinesCurrentPage = venuePage
      }

      const sheetRows = Array.from(sheetTable.$el.querySelectorAll('tbody tr'))
      const sheetRowEl = sheetRows[sheetIndex]
      if (sheetRowEl == null) return

      // Scroll the sheet row to the top of the sheet table (below the sticky header)
      const sheetHeaderHeight = this.getStickyHeaderHeight(sheetTable)
      const sheetRowOffset = sheetRowEl.getBoundingClientRect().top - sheetScroll.getBoundingClientRect().top - sheetHeaderHeight
      sheetScroll.scrollTo({
        top: sheetScroll.scrollTop + sheetRowOffset,
        behavior: 'smooth',
      })

      // After the page switch renders, scroll the venue wine row to the top
      this.waitForSettle().then(() => {
        const venueRows = Array.from(venueTable.$el.querySelectorAll('tbody tr'))
        // The venue wine is at position (venueIndex % perPage) on its page
        const venueRowEl = venueRows[venueIndex % this.venueWinesPerPage]
        if (venueRowEl == null) return

        const venueHeaderHeight = this.getStickyHeaderHeight(venueTable)
        const venueRowOffset = venueRowEl.getBoundingClientRect().top - venueScroll.getBoundingClientRect().top - venueHeaderHeight
        venueScroll.scrollTo({
          top: venueScroll.scrollTop + venueRowOffset,
          behavior: 'smooth',
        })
      })
    },
    /**
     * Attach native drag-and-drop listeners directly to the DOM elements.
     *
     * Bootstrap-vue's <b-table> root element is the .b-table-sticky-header
     * wrapper, and Vue's @dragstart/@dragover/@drop event binding on the
     * component does not reliably reach it. To make drag-and-drop work, the
     * listeners are attached here with event delegation on the corresponding
     * scroll containers.
     */
    attachDragDropListeners() {
      this.$nextTick(() => {
        // Venue wine container -> drag source (dragstart)
        const venueScroll = this.$refs.venueWineTable
          ? this.$refs.venueWineTable.$el.closest('.tableScrollContainer')
          : null
        if (venueScroll != null) {
          venueScroll.addEventListener('dragstart', this.onVenueWineDragStart)
        }

        // Sheet row container -> drop target (dragover / drop)
        const sheetScroll = this.$refs.sheetRowTable
          ? this.$refs.sheetRowTable.$el.closest('.tableScrollContainer')
          : null
        if (sheetScroll != null) {
          sheetScroll.addEventListener('dragover', this.onSheetRowDragOver)
          sheetScroll.addEventListener('drop', this.onSheetRowDrop)
          sheetScroll.addEventListener('dragleave', this.onSheetRowDragLeave)
        }
      })
    },
    detachDragDropListeners() {
      const venueScroll = this.$refs.venueWineTable
        ? this.$refs.venueWineTable.$el.closest('.tableScrollContainer')
        : null
      if (venueScroll != null) {
        venueScroll.removeEventListener('dragstart', this.onVenueWineDragStart)
      }

      const sheetScroll = this.$refs.sheetRowTable
        ? this.$refs.sheetRowTable.$el.closest('.tableScrollContainer')
        : null
      if (sheetScroll != null) {
        sheetScroll.removeEventListener('dragover', this.onSheetRowDragOver)
        sheetScroll.removeEventListener('drop', this.onSheetRowDrop)
        sheetScroll.removeEventListener('dragleave', this.onSheetRowDragLeave)
      }
    },
    /**
     * Called when a drag leaves the sheet row table. Clears the dragged id so
     * a subsequent drop outside the target does not apply a stale match.
     */
    onSheetRowDragLeave() {
      // No-op placeholder to keep preventDefault on dragover from causing
      // issues; the dragged id is retained until a drop or a new dragstart.
    },
    onAddProduct(sheetRow = null) {
      this.$refs.createWinesModal.showModal(sheetRow, this.selectedSheetType);

      Tracking.trackEvent(
        Tracking.CATEGORY_RECEIPTS,
        Tracking.ACTION_SHOW_ADD_PRODUCT,
      )
    },
    /**
     * Called when new venue wines were created in the CreateWinesModal.
     *
     * If the modal was opened from a sheet row ("Neu erstellen"), the sheet
     * row is connected to the newly created venue wine: the pqVenueWineId is
     * set to the new venue wine id and the updater status is updated, so the
     * updater will skip this row later. The updated row is then saved to the
     * AI backend.
     *
     * After the reload, the sheet row is scrolled to and its details are
     * opened, so the user sees the connected state.
     *
     * @param {Array<number>} venueWineIds The ids of the created venue wines
     * @param {Object|null} sheetRow The sheet row the modal was opened with
     */
    async handleAddedNewVenueWines(venueWineIds, sheetRow) {
      // Remember the row index of the sheet row, so it can be found again
      // after the reload.
      const rowIndex = sheetRow != null ? sheetRow.rowIndex : null

      // Connect the sheet row to the newly created venue wine, if the modal
      // was opened from a sheet row ("Neu erstellen").
      if (sheetRow != null && venueWineIds != null && venueWineIds.length > 0) {
        const venueWineId = venueWineIds[0]
        sheetRow.pqVenueWineId = venueWineId
        sheetRow.updaterStatus = `${UpdaterStatusSheetText.VenueWineCreated}: ${venueWineId}`

        if (this.sheetId != null) {
          try {
            const venueId = this.getVenue.id
            await AiService.updateSheetRows(this.sheetId, venueId, [sheetRow])
          } catch (err) {
            console.log(err)
            this.$store.commit('setAlertText', {
              text: this.$t('receiptMatcher.saveError'),
              variant: 'danger',
            })
          }
        }
      }

      await this.loadVenueWines()
      await this.loadSheetRows()

      // Scroll to the sheet row and open its details, so the user sees the
      // connected state (new venue wine id + updater status).
      if (rowIndex != null) {
        const updatedRow = this.sheetRows.find((r) => r.rowIndex === rowIndex)
        if (updatedRow != null) {
          this.toggleSheetRowDetails(updatedRow)
        }
      }
    },
  },
  async created() {
    // Load the venue wines first, so the right table is populated before any sheet rows are loaded.
    await this.loadVenueWines()

    // If the route provided a filter value (receipt date or invoice number),
    // automatically load the matching sheet rows.
    const hasInvoiceNumberAndInvoiceDate =
      this.sheetInvoiceNumber != null
      && this.sheetInvoiceNumber !== ''
      && this.sheetInvoiceDate != null
      && this.sheetInvoiceDate !== '';

    const hasReceiptDate =
      this.sheetReceiptDate != null
      && this.sheetReceiptDate !== '';

    if (hasInvoiceNumberAndInvoiceDate || hasReceiptDate) {
      await this.loadSheetRows()
    }
  },
  mounted() {
    this.attachDragDropListeners()
  },
  beforeDestroy() {
    this.detachDragDropListeners()
  },
  watch: {
    selectedSheetType() {
      // Reset the invoice date when switching to outbound
      if (this.selectedSheetType === 'outbound') {
        this.sheetInvoiceDate = ''
      }
    },
    sheetReceiptDate() {
      // Auto-load outbound sheet rows when a receipt date is set
      if (this.selectedSheetType === 'outbound' && this.sheetReceiptDate) {
        this.loadSheetRows()
      }
    },
    sheetInvoiceDate() {
      // Auto-load inbound sheet rows when an invoice date is set
      if (this.selectedSheetType === 'inbound' && this.sheetInvoiceDate) {
        this.loadSheetRows()
      }
    },
  },
}
</script>

<style scoped>
.receiptMatcherView {
  /* Do NOT set padding here - the .viewContainer already provides left padding
     for the navigation menu and right margin. Overriding it pushes the table
     behind the left menu. */
  padding-top: 20px;
  padding-bottom: 20px;
}

.receiptMatcherHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.receiptMatcherTitle {
  display: flex;
  align-items: center;
  gap: 12px;
}

.receiptMatcherTitle h1 {
  margin: 0;
}

.receiptMatcherControls {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-right: 22px;
}

.sheetTypeToggle {
  /* The global .toggleButtons styles (main.css) provide the base look.
     This scoped rule highlights the active button, like the
     .winesViewHeader .toggleButtons.wineViewFilter button.active rule. */
  width: 290px;
  height: 32px;
}

.sheetTypeToggle button.active {
  background-color: #495057;
  color: var(--color-primary-500);
}

.filterInput {
  width: 220px;
}

.receiptMatcherTables {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.receiptMatcherTableContainer {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.tableScrollContainer {
  /* Foreground, Background */
  scrollbar-color: var(--color-primary-500) #0d0d0d;
  position: relative;
}

/* Overlay the loading indicator on top of the table area instead of pushing
   it below the fixed-height table. */
.tableScrollContainer .dotfloatingContainer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
}

.tableScrollContainer .b-table-sticky-header {
  /* Fixed height so both tables always have the same height, even when the
     sheet rows table is empty or still loading. */
  height: calc(100vh - 380px);
  height: calc(100dvh - 380px);
  overflow-y: auto;
  /* Extra space after the last row so it can still be scrolled to the top */
  padding-bottom: 300px;
}

.tableScrollContainer::-webkit-scrollbar,
.tableScrollContainer .b-table-sticky-header::-webkit-scrollbar {
  width: 10px;
  /* Mostly for vertical scrollbars */
}

.tableScrollContainer::-webkit-scrollbar-thumb,
.tableScrollContainer .b-table-sticky-header::-webkit-scrollbar-thumb {
  /* Foreground */
  background: var(--color-primary-500);
  border-radius: 8px;
}

.tableScrollContainer::-webkit-scrollbar-track,
.tableScrollContainer .b-table-sticky-header::-webkit-scrollbar-track {
  /* Background */
  background: #0d0d0d;
  border-radius: 8px;
}

.tableScrollContainer .table {
  margin-bottom: 0;
}

.venueWineFooter {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-top: 20px;
  border-top: 1px solid #333;
}

.sheetRowsHelp {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  height: 200px;
  color: #888;
  font-size: 1rem;
  text-align: center;
  padding: 0 100px;
}

/* Alternating row background colors (same as VenueWines.vue) */
.receiptMatcherView ::v-deep table tr:nth-child(odd) td {
  background-color: #2b2c2d;
}

/* The download button styles live in DocumentDownload.vue. */

.tableTitleGroup {
  display: flex;
  align-items: center;
  gap:  ️8px;
  min-width: 0;
}

.tableHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 10px;
}

.tableHeader h2 {
  margin: 0;
  font-size: 1.2rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.tableSearch {
  min-width: 200px;
  max-width: 300px;
  color: var(--color-gray-150);
  background-color: var(--color-gray-300);
  height: 36px;
  max-height: 36px;
  margin-right: 22px;
}

.tableSearch::placeholder {
  color: var(--color-gray-150);
}

.tableSearchWrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.tableSearchWrapper .tableSearch {
  padding-right: 30px;
}

.tableSearchReset {
  position: absolute;
  right: 32px;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.deletedVenueWine {
  text-decoration: line-through;
}

.detailGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
  padding: 10px;
}

.detailItem {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #eee;
  padding: 4px 0;
}

.detailLabel {
  font-size: 0.75rem;
  color: #888;
  text-transform: uppercase;
}

.detailValue {
  font-size: 0.9rem;
}
</style>
